import React from 'react';
import { Modal, Card, Typography } from 'antd';
import { MessageCircle, FileText, Calendar, AlertTriangle } from 'lucide-react';
import { BookingOptionsModalProps } from '@/interfaces/ICalendarWorkflows';

const { Title, Text } = Typography;

export const BookingOptionsModal: React.FC<BookingOptionsModalProps> = ({
	open,
	onCancel,
	onSendBookingLink,
	onCollectBookingInfo,
	onOverPhoneScheduling,
}) => {
	const bookingOptions = [
		{
			id: 'send-booking-link',
			title: 'Text Booking Link',
			description:
				'Automatically send your calendar booking link via text message to callers who want to schedule appointments',
			icon: <MessageCircle size={24} className="text-blue-500" />,
			badge: 'recommended',
			onClick: onSendBookingLink,
		},
		{
			id: 'create-booking-request',
			title: 'Collect Booking Request',
			description:
				'AI collects caller availability and preferences, then creates a booking request that a human can review and manually schedule later',
			icon: <FileText size={24} className="text-green-500" />,
			badge: 'recommended',
			onClick: onCollectBookingInfo,
		},
		{
			id: 'over-phone-scheduling',
			title: 'Schedule Over Phone',
			description:
				'AI assistant books appointments directly during the call using your connected calendar system',
			icon: <Calendar size={24} className="text-purple-500" />,
			badge: 'beta',
			onClick: onOverPhoneScheduling,
		},
	];

	return (
		<Modal title="Choose Booking Method" open={open} onCancel={onCancel} footer={null} width={800}>
			<div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
				<AlertTriangle size={20} className="text-orange-600 mt-0.5 flex-shrink-0" />
				<div>
					<Text strong className="text-orange-800">
						Recommendation
					</Text>
					<Text className="block text-orange-700 text-sm mt-1">
						We recommend using only one booking method to avoid confusion for your receptionist.
						Having multiple booking workflows may lead to inconsistent experiences.
					</Text>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4">
				{bookingOptions.map((option) => (
					<Card
						key={option.id}
						className="cursor-pointer hover:shadow-lg transition-shadow duration-200 border-2 hover:border-blue-300"
						bodyStyle={{ padding: '20px' }}
						onClick={option.onClick}
					>
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0">{option.icon}</div>
							<div className="flex-1">
								<div className="flex items-center gap-2 mb-2">
									<Title level={5} className="mb-0">
										{option.title}
									</Title>
									{option.badge && (
										<span
											className={`px-2 py-1 text-xs font-medium rounded-full ${
												option.badge === 'recommended'
													? 'bg-green-100 text-green-600'
													: 'bg-orange-100 text-orange-600'
											}`}
										>
											{option.badge}
										</span>
									)}
								</div>
								<Text type="secondary" className="text-sm">
									{option.description}
								</Text>
							</div>
						</div>
					</Card>
				))}
			</div>
		</Modal>
	);
};
